import { Amplify } from 'aws-amplify';
import { getAmplifyConfig } from './auth.config.js';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { AuthCustomStorage } from './AuthCustomStorage.js';
import { updateUserAttributes as updateUserAttributes$1, signUp, signIn, confirmSignUp, resendSignUpCode, confirmResetPassword, signOut, resetPassword, signInWithRedirect, updatePassword, confirmSignIn, fetchAuthSession as fetchAuthSession$1, fetchUserAttributes as fetchUserAttributes$1 } from 'aws-amplify/auth';
import { getAuthConfig } from '../config.js';
import { keyBy, safeParse, cloneDeep } from '../utils.js';
import AuthError from '../auth.errors.js';
import useAuthStore from '../auth.store.js';
import { fetchCustomerMetadata } from '../actions/auth-action.helpers.js';
import { isWeb } from '../platform.js';

/**
 * API Docs: https://docs.aws.amazon.com/sdkfornet/v3/apidocs/items/CognitoIdentityProvider/TCognitoIdentityProviderClient.html
 */ const SUPPORTED_CONFIGURATION_MEDIUM = {
    EMAIL: 'EMAIL'
};
const configureCognito = ()=>{
    try {
        const amplifyConfig = getAmplifyConfig();
        // Configure Amplify with explicit resource config format
        Amplify.configure({
            Auth: {
                Cognito: {
                    ...amplifyConfig.Auth.Cognito,
                    // Explicitly disable SRP to force USER_PASSWORD_AUTH
                    authenticationFlowType: 'USER_PASSWORD_AUTH'
                }
            },
            ...amplifyConfig.API && {
                API: amplifyConfig.API
            }
        });
        cognitoUserPoolsTokenProvider.setKeyValueStorage(new AuthCustomStorage());
    } catch (error) {
        console.error('❌ Universal Auth: Failed to configure Amplify:', error);
        const config = getAuthConfig();
        if (config.onError) {
            config.onError(error, {
                context: 'configureCognito'
            });
        } else {
            console.error('configureCognito error:', error);
        }
    }
};
let onAuthCallInProgress = Promise.resolve();
const fetchAuthSession = async ({ forceRefresh = false } = {})=>{
    await onAuthCallInProgress;
    const deferredPromise = makeDeferredPromise();
    onAuthCallInProgress = deferredPromise.promise;
    try {
        const tokens = await cognitoUserPoolsTokenProvider.getTokens();
        if (!tokens) {
            // During OAuth flows, tokens might not be immediately available
            // Don't automatically sign out - let the OAuth completion handle it
            return null;
        }
        const session = await fetchAuthSession$1({
            forceRefresh
        });
        return session;
    } finally{
        deferredPromise.resolve();
    }
};
const signOutCognito = async (signOutInput)=>{
    if (!isWeb()) {
        unsetSignoutRedirectUrl();
    }
    // Support global signout option
    const response = await signOut(signOutInput);
    // Only reconfigure if not global signout (since tokens are invalidated)
    if (!signOutInput?.global) {
        const amplifyConfig = getAmplifyConfig();
        // @ts-ignore
        Amplify.configure({
            ...amplifyConfig
        });
    }
    return response;
};
const resendSignUpCodeCognito = async (resendSignUpCodeInput)=>{
    const response = await resendSignUpCode(resendSignUpCodeInput);
    const deliveryMedium = response.deliveryMedium;
    if (!SUPPORTED_CONFIGURATION_MEDIUM[deliveryMedium]) {
        throw new AuthError({
            message: 'Unsupported confirmation medium',
            code: 'UNSUPPORTED_CONFIRMATION_MEDIUM',
            meta: {
                deliveryMedium,
                response
            }
        });
    }
    return {
        codeMedium: deliveryMedium
    };
};
const confirmSignUpCognito = async (confirmSignUpInput)=>{
    const response = await confirmSignUp(confirmSignUpInput);
    if (response.isSignUpComplete) {
        return {
            isSignUpComplete: true
        };
    }
    if (response.nextStep.signUpStep !== 'CONFIRM_SIGN_UP') {
        throw new AuthError({
            message: 'Failed to confirm sign up',
            code: 'FAILED_TO_CONFIRM_SIGN_UP'
        });
    }
    const resendSignUpCodeResponse = await resendSignUpCodeCognito({
        username: confirmSignUpInput.username
    });
    return {
        isSignUpComplete: false,
        needFartherConfirmation: true,
        codeMedium: resendSignUpCodeResponse.codeMedium
    };
};
const signInCognito = async (signInInput)=>{
    const response = await signIn(signInInput);
    if (response.nextStep?.signInStep === 'RESET_PASSWORD') {
        return {
            isSignedIn: false,
            resetPasswordRequired: true
        };
    }
    if (response.isSignedIn) {
        return {
            isSignedIn: true
        };
    }
    // Handle custom authentication challenge
    if (response.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        return {
            isSignedIn: false,
            needCustomChallenge: true,
            nextStep: response.nextStep
        };
    }
    // Handle sign-up confirmation
    if (response.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        const resendSignUpCodeResponse = await resendSignUpCodeCognito({
            username: signInInput.username
        });
        return {
            isSignedIn: false,
            needSignUpConfirmation: true,
            codeMedium: resendSignUpCodeResponse.codeMedium
        };
    }
    // Handle other cases as errors
    throw new AuthError({
        message: 'Failed to sign in',
        code: 'FAILED_TO_SIGN_IN',
        meta: {
            response
        }
    });
};
const signUpCognito = async (signUpInput)=>{
    const response = await signUp(signUpInput);
    if (response.isSignUpComplete) {
        return {
            isSignUpComplete: true
        };
    }
    if (response.nextStep.signUpStep !== 'CONFIRM_SIGN_UP') {
        throw new AuthError({
            message: 'Failed to sign up',
            code: 'FAILED_TO_SIGN_UP',
            meta: {
                response
            }
        });
    }
    const deliveryMedium = response.nextStep.codeDeliveryDetails.deliveryMedium;
    if (!SUPPORTED_CONFIGURATION_MEDIUM[deliveryMedium]) {
        throw new AuthError({
            message: 'Unsupported confirmation medium',
            code: 'UNSUPPORTED_CONFIRMATION_MEDIUM',
            meta: {
                deliveryMedium,
                response
            }
        });
    }
    return {
        isSignUpComplete: false,
        needSignUpConfirmation: true,
        codeMedium: deliveryMedium
    };
};
const resetPasswordCognito = async (resetPasswordInput)=>{
    return await resetPassword(resetPasswordInput);
};
const confirmResetPasswordCognito = async (confirmResetPasswordInput)=>{
    return await confirmResetPassword(confirmResetPasswordInput);
};
const signInWithRedirectCognito = async (signInWithRedirectInput)=>{
    // Ensure Amplify is configured before attempting OAuth
    try {
        // Check if Amplify is configured by trying to get the current config
        const currentConfig = Amplify.getConfig();
        if (!currentConfig?.Auth?.Cognito?.userPoolId) {
            configureCognito();
        }
    } catch (error) {
        configureCognito();
    }
    // Just initiate the redirect - don't try to fetch session immediately
    // The session will be available after the OAuth callback completes
    const response = await signInWithRedirect(signInWithRedirectInput);
    return response;
};
// @deprecated // For Webview only
const fetchUserAttributes = async ()=>{
    const userAttributes = await fetchUserAttributes$1();
    return userAttributes;
};
/**
 * Get current authenticated user information
 * Replaces Auth.currentAuthenticatedUser() from Amplify v5
 * @returns User information including attributes and session data
 */ const getCurrentUser = async (forceRefresh = false)=>{
    try {
        const session = await fetchAuthSession({
            forceRefresh
        });
        const [userAttributes, customerMetadata] = await Promise.all([
            fetchUserAttributes$1(),
            fetchCustomerMetadata()
        ]);
        const idToken = session?.tokens?.idToken;
        const accessToken = session?.tokens?.accessToken;
        const user = {
            // User attributes (email, name, etc.)
            attributes: userAttributes,
            // Customer information
            customerMetadata,
            // Username from token
            username: idToken?.payload?.['cognito:username'] || userAttributes?.email || 'unknown',
            // User email
            email: userAttributes?.email,
            // User first name
            family_name: userAttributes?.family_name,
            // User last name
            given_name: userAttributes?.given_name || userAttributes?.name,
            // User name
            name: userAttributes?.name || userAttributes?.given_name,
            // Token information
            tokens: {
                idToken: idToken?.toString(),
                accessToken: accessToken?.toString()
            },
            // Token payload data
            payload: idToken?.payload,
            // User ID and sub
            userId: idToken?.payload?.sub,
            // Custom attributes
            customerId: idToken?.payload?.['custom:customer_id'],
            // Provider information for federated users
            identities: userAttributes?.identities ? JSON.parse(userAttributes.identities) : [],
            // Check if federated user
            isFederated: ()=>{
                try {
                    const identities = userAttributes?.identities ? JSON.parse(userAttributes.identities) : [];
                    return Array.isArray(identities) && identities.some((identity)=>(identity?.providerName === 'Google' || identity?.providerName === 'SignInWithApple') && !!identity.primary);
                } catch  {
                    return false;
                }
            }
        };
        // Cache the user data in Zustand store
        useAuthStore.getState().setUser(user);
        return user;
    } catch (error) {
        useAuthStore.getState().clearUser();
        const config = getAuthConfig();
        if (config.onError) {
            config.onError(error, {
                context: 'getCurrentUser',
                message: 'Error getting current user'
            });
        } else {
            console.error('getCurrentUser error:', error);
        }
        return null;
    }
};
/**
 * Update user attributes in Cognito
 * Replaces Auth.updateUserAttributes() from Amplify v5
 */ const updateUserAttributes = async (attributes)=>{
    try {
        const response = await updateUserAttributes$1({
            userAttributes: attributes
        });
        return response;
    } catch (error) {
        const config = getAuthConfig();
        if (config.onError) {
            config.onError(error, {
                context: 'updateUserAttributes',
                message: 'Error updating user attributes',
                attributes
            });
        } else {
            console.error('updateUserAttributes error:', error);
        }
        throw error;
    }
};
/**
 * Change user password in Cognito
 * Replaces Auth.changePassword() from Amplify v5
 */ const changePassword = async (oldPassword, newPassword)=>{
    try {
        const response = await updatePassword({
            oldPassword,
            newPassword
        });
        return response;
    } catch (error) {
        const config = getAuthConfig();
        if (config.onError) {
            config.onError(error, {
                context: 'changePassword',
                message: 'Error changing password'
            });
        } else {
            console.error('changePassword error:', error);
        }
        throw error;
    }
};
/**
 * Helper function to check if password reset is required
 * Checks the custom:force_reset_pw attribute from token payload
 */ const isPasswordResetRequired = async ()=>{
    try {
        const user = await getCurrentUser();
        return user?.payload?.['custom:force_reset_pw'] === 'true';
    } catch (error) {
        return false;
    }
};
/**
 * Helper function to clear the password reset flag
 * Sets custom:force_reset_pw to 'false'
 */ const clearPasswordResetFlag = async ()=>{
    await updateUserAttributes({
        'custom:force_reset_pw': 'false'
    });
};
// @deprecated // For Webview only
const getAuthKeyValues = async ()=>{
    const allAuthKeyValues = cognitoUserPoolsTokenProvider.authTokenStore.getKeyValueStorage().getAllValues();
    return allAuthKeyValues;
};
// @deprecated // For Webview only
const getRefreshToken = async ()=>{
    try {
        const authSession = await fetchAuthSession();
        const aud = authSession?.tokens?.idToken?.payload.aud;
        const authStorage = cognitoUserPoolsTokenProvider.authTokenStore.getKeyValueStorage();
        const allAuthKeyValues = authStorage.getAllValues();
        const allAuthKeyValuesByKey = keyBy(allAuthKeyValues, 'key');
        const lastAuthUserKey = `CognitoIdentityServiceProvider.${aud}.LastAuthUser`;
        const lastAuthUser = safeParse(allAuthKeyValuesByKey[lastAuthUserKey]?.value || '', undefined);
        const refreshTokenKey = `CognitoIdentityServiceProvider.${aud}.${lastAuthUser}.refreshToken`;
        const refreshToken = safeParse(allAuthKeyValuesByKey[refreshTokenKey]?.value || '', undefined);
        if (refreshToken) {
            return refreshToken;
        }
        // Fallback: Try to find any refresh token
        const refreshTokenFallback = safeParse(allAuthKeyValues.find((item)=>item.key.endsWith('refreshToken'))?.value || '', undefined);
        if (!refreshTokenFallback) {
            const config = getAuthConfig();
            const error = new Error('NO_REFRESH_TOKEN_FALLBACK');
            if (config.onError) {
                config.onError(error, {
                    context: 'getRefreshToken_fallback',
                    aud,
                    lastAuthUser
                });
            } else {
                console.error('getRefreshToken fallback error:', error);
            }
            return 'not_found';
        }
        return refreshTokenFallback;
    } catch (error) {
        const config = getAuthConfig();
        if (config.onError) {
            config.onError(error, {
                context: 'getRefreshToken'
            });
        } else {
            console.error('getRefreshToken error:', error);
        }
        return 'not_found';
    }
};
const unsetSignoutRedirectUrl = ()=>{
    const config = cloneDeep(Amplify.getConfig());
    if (config.Auth?.Cognito?.loginWith?.oauth?.redirectSignOut) {
        //@ts-ignore
        config.Auth.Cognito.loginWith.oauth.redirectSignOut = undefined;
    }
    Amplify.configure(config);
};
const makeDeferredPromise = ()=>{
    let resolve = ()=>{}, reject = ()=>{};
    const promise = new Promise((_resolve, _reject)=>{
        resolve = _resolve;
        reject = _reject;
    });
    return {
        promise,
        resolve,
        reject
    };
};
/**
 * Confirm sign in with OTP/code using modern Amplify v6 approach
 * This completes the custom authentication flow
 *
 * Usage:
 * await confirmSignInWithCodeCognito({ challengeResponse: '123456' });
 */ const confirmSignInWithCodeCognito = async (input)=>{
    try {
        const response = await confirmSignIn(input);
        return response;
    } catch (error) {
        const config = getAuthConfig();
        if (config.onError) {
            config.onError(error, {
                context: 'confirmSignInWithCodeCognito',
                message: 'Error confirming sign in with code',
                code: input.challengeResponse
            });
        } else {
            console.error('confirmSignInWithCodeCognito error:', error);
        }
        throw error;
    }
};

export { changePassword, clearPasswordResetFlag, configureCognito, confirmResetPasswordCognito, confirmSignInWithCodeCognito, confirmSignUpCognito, fetchAuthSession, fetchUserAttributes, getAuthKeyValues, getCurrentUser, getRefreshToken, isPasswordResetRequired, resendSignUpCodeCognito, resetPasswordCognito, signInCognito, signInWithRedirectCognito, signOutCognito, signUpCognito, updateUserAttributes };
//# sourceMappingURL=cognito.helper.js.map
